import type { GroupId } from "@repo/app-types/entities/group";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import type { FC } from "react";
import { useMemo } from "react";

export interface GroupOption {
  id: GroupId;
  label: string;
}

export interface GroupPickerProps {
  placeholder: string;
  /** Accessible name for the combobox; the visible field label lives outside. */
  ariaLabel: string;
  groupOptions: readonly GroupOption[];
  value: readonly GroupId[];
  onChange: (groupIds: readonly GroupId[]) => void;
  /**
   * While the caller's options are still loading, disable the control and show a
   * loading placeholder. The picker only ever renders the options it's handed —
   * it never substitutes a fallback set — so this keeps an in-flight fetch from
   * looking like an empty (or wrong) list.
   */
  isLoading?: boolean;
  /** Renders the picker disabled, e.g. for read-only viewers. */
  isDisabled?: boolean;
}

/**
 * Multi-select of teammate groups with type-to-filter and the current selection
 * rendered as removable chips — the group counterpart to {@link TeammatePicker},
 * used for the custom automation's "Notify" groups.
 */
export const GroupPicker: FC<GroupPickerProps> = (props) => {
  const {
    placeholder,
    ariaLabel,
    groupOptions,
    value,
    onChange,
    isLoading,
    isDisabled,
  } = props;
  const selectedIds = useMemo(() => [...value], [value]);

  return (
    <div className="w-full sm:max-w-md">
      <ComboboxField<GroupOption, "multiple">
        aria-label={ariaLabel}
        selectionMode="multiple"
        items={groupOptions}
        getLabel={(group) => group.label}
        value={selectedIds}
        onChange={(keys) => {
          // Report the ids in options order, so a re-selection can't look like
          // a change to callers that compare the arrays.
          const nextIds = new Set(keys.map(String));
          onChange(
            groupOptions
              .filter(({ id }) => nextIds.has(id))
              .map(({ id }) => id),
          );
        }}
        placeholder={isLoading ? "Loading…" : placeholder}
        chipsLabel={`Selected ${ariaLabel.toLowerCase()}`}
        isDisabled={isLoading || isDisabled}
      />
    </div>
  );
};
